/**
 * AuthProvider — JWT-based auth state.
 * Checks learnature_token cookie or localStorage, decodes JWT client-side.
 * Server validates tokens — client only reads claims for UI.
 */

import type { AuthState, UserInfo, ProfileInfo } from './AweniContext';

const TOKEN_KEY = 'learnature_token';
const PROFILE_KEY = 'aweni_active_profile';
const REFRESH_URL = '/identity/token/refresh';

export class AuthProvider implements AuthState {
  isAuthenticated = false;
  user: UserInfo | null = null;
  activeProfile: ProfileInfo | null = null;
  profiles: ProfileInfo[] = [];
  private _token: string | null = null;
  private _refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private _listeners: Array<() => void> = [];

  constructor() {
    this._token = this._findToken();
    if (this._token) {
      this._decodeAndSet(this._token);
      this._scheduleRefresh();
    }
    this._loadProfiles();

    // Listen for auth changes from AuthModule (login success)
    window.addEventListener('aweni:auth-changed', () => {
      const newToken = this._findToken();
      if (newToken && newToken !== this._token) {
        this._token = newToken;
        this._decodeAndSet(this._token);
        this._scheduleRefresh();
        this._loadProfiles();
        this._notify();
      }
    });
  }

  getToken(): string | null {
    return this._token;
  }

  /**
   * Navigate to AWENI's built-in login page.
   * Saves the current path so user returns after login.
   */
  login(): void {
    const returnPath = window.location.hash.replace(/^#/, '') || '/home';
    sessionStorage.setItem('aweni_auth_return', returnPath);
    window.location.hash = '#/entry';
  }

  async logout(): Promise<void> {
    try {
      if (this._token) {
        await fetch('/identity/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this._token}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });
      }
    } catch {
      // Logout even if server call fails
    }

    this._token = null;
    this.isAuthenticated = false;
    this.user = null;
    this.activeProfile = null;
    this.profiles = [];

    // Clear stored tokens
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(PROFILE_KEY);
    this._clearCookie(TOKEN_KEY);

    if (this._refreshTimer) {
      clearTimeout(this._refreshTimer);
      this._refreshTimer = null;
    }

    this._notify();
  }

  async switchProfile(profileId: number): Promise<void> {
    if (!this._token) return;

    try {
      const res = await fetch('/identity/profiles/switch', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this._token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ profile_id: profileId }),
      });

      if (res.ok) {
        const data = await res.json();
        if (data.token) {
          this._token = data.token;
          localStorage.setItem(TOKEN_KEY, this._token!);
          this._decodeAndSet(this._token!);
        }
        this.activeProfile = this.profiles.find(p => p.id === profileId) || null;
        localStorage.setItem(PROFILE_KEY, String(profileId));
        this._notify();
      }
    } catch {
      // Silent fail — profile switch is non-critical
    }
  }

  onChange(cb: () => void): void {
    this._listeners.push(cb);
  }

  private _findToken(): string | null {
    // Check localStorage first
    const stored = localStorage.getItem(TOKEN_KEY);
    if (stored && this._isValidJWT(stored)) return stored;

    // Check cookies
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === TOKEN_KEY && value && this._isValidJWT(value)) {
        return value;
      }
    }

    return null;
  }

  private _isValidJWT(token: string): boolean {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    try {
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      // Check expiry
      if (payload.exp && payload.exp * 1000 < Date.now()) return false;
      return true;
    } catch {
      return false;
    }
  }

  private _decodeAndSet(token: string): void {
    try {
      const parts = token.split('.');
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));

      this.isAuthenticated = true;
      const isGuest = payload.is_guest === 1 || payload.is_guest === true;
      const rawEmail = payload.email || '';
      // Hide synthetic guest email from UI
      const displayEmail = isGuest && rawEmail.endsWith('@guest.learnature-ly.local') ? '' : rawEmail;
      this.user = {
        id: payload.sub || payload.user_id || 0,
        email: displayEmail,
        name: payload.name || payload.display_name || '',
        role: payload.role || 'user',
        avatar_url: payload.avatar_url,
        is_guest: isGuest,
        email_verified: !!payload.email_verified_at || !!payload.email_verified,
      };
    } catch {
      this.isAuthenticated = false;
      this.user = null;
    }
  }

  private async _loadProfiles(): Promise<void> {
    if (!this._token) return;

    try {
      const res = await fetch('/identity/profiles', {
        headers: { 'Authorization': `Bearer ${this._token}` },
        credentials: 'include',
      });

      if (res.ok) {
        const data = await res.json();
        this.profiles = (data.profiles || data || []).map((p: Record<string, unknown>) => ({
          id: p.id as number,
          name: (p.display_name || p.name || 'Profile') as string,
          avatar_url: p.avatar_url as string | undefined,
          type: (p.type || 'owner') as ProfileInfo['type'],
          role_badge: p.role_badge as string | undefined,
        }));

        // Restore saved active profile
        const savedId = localStorage.getItem(PROFILE_KEY);
        if (savedId) {
          this.activeProfile = this.profiles.find(p => p.id === Number(savedId)) || this.profiles[0] || null;
        } else {
          this.activeProfile = this.profiles[0] || null;
        }
        this._notify();
      }
    } catch {
      // Profiles will just be empty — non-critical
    }
  }

  private _scheduleRefresh(): void {
    if (!this._token) return;

    try {
      const parts = this._token.split('.');
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (!payload.exp) return;

      // Refresh 2 minutes before expiry
      const expiresIn = (payload.exp * 1000) - Date.now() - 120_000;
      if (expiresIn <= 0) {
        this._attemptRefresh();
        return;
      }

      this._refreshTimer = setTimeout(() => this._attemptRefresh(), expiresIn);
    } catch {
      // No refresh scheduling on parse failure
    }
  }

  private async _attemptRefresh(): Promise<void> {
    try {
      const res = await fetch(REFRESH_URL, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (res.ok) {
        const data = await res.json();
        if (data.token) {
          this._token = data.token;
          localStorage.setItem(TOKEN_KEY, this._token!);
          this._decodeAndSet(this._token!);
          this._scheduleRefresh();
          this._notify();
        }
      } else {
        // Refresh failed — user needs to re-login
        await this.logout();
      }
    } catch {
      // Network error during refresh — will retry on next navigation
    }
  }

  private _clearCookie(name: string): void {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  }

  private _notify(): void {
    for (const cb of this._listeners) {
      cb();
    }
  }
}
